import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Typography, TextField, InputAdornment, IconButton, Tooltip, Grid } from "@material-ui/core";
import { 
    Page, 
    PageContent, 
    ScrollToTopOnMount,
    PopupSnackbar,
	PageHeaderListActions,
} from "frame/components";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import { PageHeaderTabs } from "features/health-safety-forms/components";
import { useRouter, useIsOnline, useTerminology } from "utils";
import SiteInspectionChecklistCard from "./site-inspection-checklist-card";
import EditSiteInspectionChecklistForm from "../edit-site-inspection-checklist-form";
import * as fromHealthSafetyForm from "features/health-safety-forms/actions/types";
import * as fromHealthAndSafety from "features/health-and-safety/actions/types";
import { SendEmailModal } from "frame/components";
import { useSelector } from "react-redux";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { initialJobState } from "features/health-and-safety/reducers/health-safety-reducer";
import { getPageResults } from "utils";
import { CardText } from "features/timesheet/components/timesheet-list";
import InfiniteScroll from "react-infinite-scroll-component";

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: "none",
    },
    filter: { 
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
    },
    empty: {
        marginTop: theme.spacing(2),
        textAlign: "center",
    },
    search: {
        display: "flex"
    },
    gridContainer: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("xs")]: {
            marginTop: theme.spacing(3)
        }
    },
    filterGrid: {
        paddingTop: theme.spacing(1)
    },
}));

const SiteInspectionChecklistList = ({ 
    siteInspectionChecklists, 
    role, 
    onDelete, 
    onSearchChanged,
    searchFilter,
    onGetSiteInspectionChecklistsList,
    onGetSitesList,
    sites,
    currentOrganisationGUID
}) => {
    const classes = useStyles();
    const isOnline = useIsOnline();
    const { siteLabel } = useTerminology();
    const { match } = useRouter();
    const orgFromSelector = useSelector(state => state.organisations.currentOrganisation);
    const currentOrgGUID = currentOrganisationGUID || (orgFromSelector?.id || orgFromSelector);

    const [editId, setEditId] = useState();
    const [viewId, setViewId] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [localSearchTerm, setLocalSearchTerm] = useState("");
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [siteId, setSiteId] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [previousSaveSuccess, setPreviousSaveSuccess] = useState(false);
    const isInitialMount = useRef(true);

    const [siteInspectionChecklistList, setSiteInspectionChecklistList] = useState(getPageResults(initialJobState));
    const [currentPage, setCurrentPage] = useState(1);
    const defaultPageSize = 10;
    const [totalNumberOfRecords, setTotalNumberOfRecords] = useState(1);
    const [hasMoreChecklists, setHasMoreChecklists] = useState(true);
    const [isDoneFetching, setIsDoneFetching] = useState(false);
    const ref = useRef(null);
    const [height, setHeight] = useState(0);

    // Load sites data for lookup
    useEffect(() => {
        if (onGetSitesList) {
            onGetSitesList();
        }
    }, [onGetSitesList]);

    const siteNames = {};
    if (sites && sites.results) {
        sites.results.forEach(site => {
            siteNames[site.id] = site.siteName;
        });
    }

    const setNewChecklistState = (checklistResult, page) => {
        let newState = initialJobState;

        if (checklistResult && checklistResult.results && checklistResult.results.length > 0) {
            newState = {
                ...newState,
                ...checklistResult
            };
        }

        newState.refreshList = checklistResult.refreshList;
        newState.flag = "done";
        // If navigating to first page clear the results from siteInspectionChecklistList otherwise add to the end of it
        if (page === 1) {
            setSiteInspectionChecklistList([...getPageResults(newState)]);
        } else {
            setSiteInspectionChecklistList([...siteInspectionChecklistList, ...getPageResults(newState)]);
        }
        setIsDoneFetching(true);
    };

    const getSiteInspectionChecklists = (
        fromPageToRequest = currentPage,
        recordDeletedOrUpdated = false,
        pageSize = defaultPageSize
    ) => {
        if (totalNumberOfRecords !== siteInspectionChecklistList.length || recordDeletedOrUpdated) {
            if (recordDeletedOrUpdated) {
                setHasMoreChecklists(true);
            }

            if (!currentOrgGUID) {
                console.warn("No organization GUID available");
                return;
            }

            safetyWiseApi
                .post(`${currentOrgGUID}/site-inspection-checklists/paged`, {
                    ascending: false,
                    page: fromPageToRequest,
                    pageSize,
                    orderBy: "createdAt",
                    searchKey: localSearchTerm
                })
                .then((result) => {
                    setNewChecklistState(result.data, fromPageToRequest);

                    if (pageSize > defaultPageSize && fromPageToRequest === 1) {
                        setCurrentPage(pageSize / defaultPageSize);
                    } else {
                        setCurrentPage(fromPageToRequest);
                    }

                    setTotalNumberOfRecords(result.data.totalNumberOfRecords);
                    if (
                        result.data.totalNumberOfRecords ==
                        siteInspectionChecklistList.length + result.data.results.length
                    ) {
                        setHasMoreChecklists(false);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching site inspection checklists:", error);
                    setErrorMessage("Failed to load site inspection checklists. Please try again.");
                    setIsDoneFetching(true);
                    setHasMoreChecklists(false);
                });
        } else {
            setHasMoreChecklists(false);
        }
    };

    // The InfiniteScroll Component requires a height for it to work effectively and for it resize relative to the parent.
    useEffect(() => {
        const handleResize = () => {
            if (ref.current) {
                setHeight(ref.current.offsetHeight - 20);
            }
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Initial load - wait for currentOrgGUID to be available
    useEffect(() => {
        if (currentOrgGUID) {
            getSiteInspectionChecklists();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrgGUID]);

    // Filter checklists based on local search term
    const filteredChecklistList = React.useMemo(() => {
        if (!localSearchTerm) return siteInspectionChecklistList;
        const lowerSearchTerm = localSearchTerm.toLowerCase();
        return siteInspectionChecklistList.filter(item => {
            const inspectorMatch = item.inspector && 
                item.inspector.toLowerCase().includes(lowerSearchTerm);
            const locationMatch = item.location && 
                item.location.toLowerCase().includes(lowerSearchTerm);
            const siteName = siteNames[item.siteId] || "";
            const siteNameMatch = siteName.toLowerCase().includes(lowerSearchTerm);
            return inspectorMatch || locationMatch || siteNameMatch;
        });
    }, [siteInspectionChecklistList, localSearchTerm, siteNames]);

    const empty = filteredChecklistList && filteredChecklistList.length <= 0;

    const onView = (id) => () => setViewId(id);
    const onEdit = (id) => () => setEditId(id);
    const onCancelEdit = () => {
        setEditId(null);
        setViewId(null);
    };
    const handleSearch = (searchText) => {
        setLocalSearchTerm(searchText);
        if (onSearchChanged) {
            onSearchChanged(searchText);
        }
    };

    // Debounced search effect - triggers search after user stops typing
    useEffect(() => {
        // Skip debounce on initial mount - initial load is handled by the first useEffect
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const searchTimeout = setTimeout(() => {
            // Reset to first page when search changes
            setCurrentPage(1);
            setSiteInspectionChecklistList([]);
            setTotalNumberOfRecords(1);
            setHasMoreChecklists(true);
            setIsDoneFetching(false);
            getSiteInspectionChecklists(1, true);
        }, 500); // 500ms debounce

        return () => clearTimeout(searchTimeout);
    }, [localSearchTerm]);
    // Email modal logic
    const handleEmail = (id, siteId) => {
        setSelectedItemId(id);
        setSiteId(siteId);
        setShowEmailModal(true);
    };

    // Error handler for email sending, matching Safety Plan Checklist
    const handleApiError = (error) => {
        if (error.response && error.response.status === 401) {
            setErrorMessage("You are not authorized to perform this action.");
        } else if (error.response && error.response.status === 404) {
            setErrorMessage("The record no longer exists.");
        } else {
            setErrorMessage(
                "Something went wrong, please refresh the page and try again."
            );
        }
    };

    return (
        <Page className={classes.root} title="Site Inspections" style={{ height: "100%" }}>
            <div ref={ref} style={{ backgroundColor: "", height: "100%", overflow: "hidden" }}>
                <InfiniteScroll
                    dataLength={filteredChecklistList.length}
                    next={() => {
                        getSiteInspectionChecklists(currentPage + 1);
                    }}
                    hasMore={hasMoreChecklists}
                    loader={<CardText message={"Loading..."} />}
                    height={height + "px"}
                    scrollThreshold={0.99}
                    style={{ paddingBottom: "10px" }}
                >
                    <ScrollToTopOnMount />
                                            <PageHeaderTabs
                            title="Site Inspections"
                            addTabPath={fromHealthSafetyForm.ADD_SITE_INSPECTION_CHECKLIST_ROUTE}
                            listTabPath={match.url}
                            hideListTabButton={true}
                            backActionPath={fromHealthAndSafety.INFO_ROUTE}
                        
						titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}>
                            {/* START: Search */}
                            {showFilters && (
                                <div className={classes.gridContainer}>
                                    <Grid container spacing={2} className={classes.filterGrid}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                className={classes.search}
                                                id="input-with-icon-textfield"
                                                variant="outlined"
                                                label="Search"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: localSearchTerm && (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                aria-label="clear search"
                                                                onClick={() => {
                                                                    setLocalSearchTerm("");
                                                                    setCurrentPage(1);
                                                                    setSiteInspectionChecklistList([]);
                                                                    setTotalNumberOfRecords(1);
                                                                    setHasMoreChecklists(true);
                                                                    setIsDoneFetching(false);
                                                                    getSiteInspectionChecklists(1, true);
                                                                }}
                                                                edge="end"
                                                                size="small"
                                                            >
                                                                <ClearIcon />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                value={localSearchTerm}
                                                onChange={(val) => {
                                                    const { value } = val.target;
                                                    setLocalSearchTerm(value);
                                                    // Real-time search with debounce effect will handle the API call
                                                }}
                                                style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
                                                placeholder={`e.g. ${siteLabel} Name, Inspector, Location`}
                                            />
                                        </Grid>
                                    </Grid>
                                </div>
                            )}
                            {/* END: Search */}
                        </PageHeaderTabs>
                    <PageContent pad>
                        {/* <PaginationHeader
                            hidePagination
                            pagination={siteInspectionChecklists}
                        /> */}
                        <p style={{ marginTop: 5 }}>
                            {filteredChecklistList.length > 0 ? totalNumberOfRecords + " records" : "No records loaded"}
                        </p>
                        {!empty && isDoneFetching && (
                            <div>
                                {filteredChecklistList.map((item, index) => (
                                    <SiteInspectionChecklistCard
                                        key={index}
                                        siteInspectionChecklist={item}
                                        onView={onView(item.id)}
                                        onEdit={onEdit(item.id)}
                                        onDelete={() => {
                                            const pageSizeToLoad =
                                                Math.ceil((index + 1) / defaultPageSize) * defaultPageSize;
                                            safetyWiseApi
                                                .del(`${currentOrgGUID}/site-inspection-checklists/delete/${item.id}`)
                                                .then(() => {
                                                    getSiteInspectionChecklists(1, true, pageSizeToLoad);
                                                })
                                                .catch((error) => {
                                                    console.error("Error deleting checklist:", error);
                                                    handleApiError(error);
                                                });
                                        }}
                                        role={role}
                                        onEmail={() => handleEmail(item.id, item.siteId)}
                                    />
                                ))}
                            </div>
                        )}
                        {empty && isDoneFetching && (
                            <Typography className={classes.empty} variant="body1">
                                There are currently no Site Inspection Checklists listed.
                            </Typography>
                        )}
                    </PageContent>
                    {viewId && (
                        <EditSiteInspectionChecklistForm
                            id={viewId}
                            initialData={filteredChecklistList.find((t) => t.id === viewId)}
                            readOnly
                            onClose={onCancelEdit}
                        />
                    )}
                    {editId && (
                        <EditSiteInspectionChecklistForm
                            id={editId}
                            initialData={filteredChecklistList.find((t) => t.id === editId)}
                            onClose={(submitted = false) => {
                                onCancelEdit();
                                if (submitted) {
                                    const checklistIndex = siteInspectionChecklistList.findIndex((t) => t.id === editId);
                                    const pageSizeToLoad =
                                        checklistIndex >= 0
                                            ? Math.ceil((checklistIndex + 1) / defaultPageSize) * defaultPageSize
                                            : defaultPageSize;
                                    getSiteInspectionChecklists(1, true, pageSizeToLoad);
                                    setSuccessMessage("Checklist updated successfully.");
                                }
                            }}
                            onSuccess={(message) => setSuccessMessage(message)}
                        />
                    )}
                    <SendEmailModal
                        isOpen={showEmailModal}
                        title={"Email Site Inspection Checklist"}
                        onClose={() => setShowEmailModal(false)}
                        onSend={(selectedRecipients) => {
                            setIsSendingEmail(true);
                            safetyWiseApi
                                .post(
                                    `${currentOrgGUID}/site-inspection-checklists/email/${selectedItemId}`,
                                    {
                                        toInternalAddresses: selectedRecipients.recipients,
                                        toExternalAddresses: [
                                            ...selectedRecipients.recipientsOther,
                                            ...selectedRecipients.recipientsCustomerSites,
                                        ],
                                    }
                                )
                                .then(() => {
                                    setSuccessMessage("Emails sent successfully.");
                                })
                                .catch(handleApiError)
                                .finally(() => {
                                    setIsSendingEmail(false);
                                    setShowEmailModal(false);
                                });
                        }}
                        isSaving={isSendingEmail}
                        selectedItemId={selectedItemId}
                        siteId={siteId}
                        emailListType={"allCustomerSiteRecord"}
                    />
                    <PopupSnackbar
                        message={errorMessage}
                        coloured
                        error
                        onExited={() => setErrorMessage("")}
                    />
                    <PopupSnackbar
                        message={successMessage}
                        coloured
                        success
                        onExited={() => setSuccessMessage("")}
                    />
                    {!isDoneFetching && <CardText message={"Loading..."} style={{ marginTop: "20px" }} />}
                    {!hasMoreChecklists && <CardText message={"All site inspection checklists have been loaded."} />}
                    {filteredChecklistList.length === 0 && isDoneFetching && (
                        <CardText message={"No site inspection checklists to show."} />
                    )}
                </InfiniteScroll>
            </div>
        </Page>
    );
};

SiteInspectionChecklistList.propTypes = {
    siteInspectionChecklists: PropTypes.object,
    role: PropTypes.string.isRequired,
    onDelete: PropTypes.func.isRequired,
    onSearchChanged: PropTypes.func,
    searchFilter: PropTypes.string,
    onGetSiteInspectionChecklistsList: PropTypes.func,
    onGetSitesList: PropTypes.func,
    sites: PropTypes.object,
    currentOrganisationGUID: PropTypes.string
};

export default SiteInspectionChecklistList; 